import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Star, Cake, Heart, Sparkles, MessageCircle, TrendingUp } from 'lucide-react'
import styles from './ClientEventsViewer.module.css'
import AuthContext from '../../store/auth-context'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import { smPost } from '../../Content/SpecialMoments/smApi'
import { emojiForIcon, prettifyOccasionType } from '../../Content/SpecialMoments/smIcons'

const SM_GET_DASHBOARD_SUMMARY = 'sm_get_dashboard_summary'

// "Today's Celebration Banner" — the warm micro-feature that surfaces
// Special Moments on the main Overview dashboard so owners see it
// every morning without opening the module. Secondary/supplementary
// widget on a busy page, so it fails quietly (no loud loading/error
// boxes) rather than fighting for attention with the rest of Overview.
const ClientEventsViewer = () => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [summary, setSummary] = useState(null)
    const [loading, setLoading] = useState(true)
    const [failed, setFailed] = useState(false)

    const loadSummary = useCallback(async () => {
        setLoading(true)
        setFailed(false)
        const res = await smPost(authCtx, activeStore, SM_GET_DASHBOARD_SUMMARY)
        if (res?.status === 'success' && res?.response) {
            setSummary(res.response)
        } else {
            setFailed(true)
        }
        setLoading(false)
    }, [authCtx, activeStore])

    useEffect(() => {
        if (activeStore?.storeIdentifier) loadSummary()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier])

    if (loading || failed || !summary) return null

    const {
        today, week, month,
        todays_preview: todaysPreview = [],
        has_occasion_data: hasOccasionData,
    } = summary

    // First-time zero state — nobody has entered a birthday/anniversary yet.
    // Unlike SMSetupState (used inside the Special Moments module itself,
    // where the visitor already knows what the feature is), this is most
    // people's very first introduction to it, right on the main dashboard —
    // so it spells out what it does and why it's worth the two minutes of
    // setup, instead of just a one-line "nothing here yet" prompt.
    if (hasOccasionData === false) {
        return (
            <div className={styles.wrapper}>
                <div className={styles.valueEmptyState}>
                    <div className={styles.valueEmptyHeader}>
                        <div className={styles.valueEmptyIcon}>
                            <Sparkles size={20} />
                        </div>
                        <div className={styles.valueEmptyHeaderText}>
                            <div className={styles.valueEmptyTitle}>Never miss a client's birthday again</div>
                            <p className={styles.valueEmptyDesc}>
                                Special Moments quietly tracks every client's birthday and anniversary, then
                                puts today's celebrations right here on your dashboard — so reaching out takes
                                one tap, not a spreadsheet.
                            </p>
                        </div>
                    </div>

                    <div className={styles.valueEmptyBullets}>
                        <div className={styles.valueEmptyBullet}>
                            <Cake size={15} />
                            <span>Automatic tracking — add it once, never forget again</span>
                        </div>
                        <div className={styles.valueEmptyBullet}>
                            <MessageCircle size={15} />
                            <span>Send a personal wish in one tap over WhatsApp or SMS</span>
                        </div>
                        <div className={styles.valueEmptyBullet}>
                            <TrendingUp size={15} />
                            <span>Clients who feel remembered come back more often</span>
                        </div>
                    </div>

                    <Link to="/manage/clients" className={styles.valueEmptyCta}>
                        Add Client Birthdays <ArrowRight size={14} />
                    </Link>
                </div>
            </div>
        )
    }

    const preview = todaysPreview.slice(0, 3)

    return (
        <div className={styles.wrapper}>
            <Link to="/special-moments" className={styles.banner}>
                <div className={styles.bannerLeft}>
                    <div className={styles.bannerTitle}>🎉 Today We Celebrate</div>
                    <div className={styles.bannerCounts}>
                        <span>{today.birthdays} Birthdays</span>
                        <span className={styles.bannerDot}>•</span>
                        <span>{today.anniversaries} Anniversaries</span>
                    </div>
                    <div className={styles.bannerTagline}>Let's make someone's day special ❤️</div>
                </div>
                <span className={styles.openLink}>
                    Open Special Moments <ArrowRight size={14} />
                </span>
            </Link>

            {/* Always visible — a clear heads-up on volume even on days
                with no individual customer to preview below. */}
            <div className={styles.summaryGrid}>
                <Link to="/special-moments/inbox" className={styles.summaryCard}>
                    <div className={styles.summaryCardLabel}>Today</div>
                    <div className={styles.summaryCardRow}>
                        <span className={styles.summaryPill}><Cake size={12} />{today.birthdays}</span>
                        <span className={styles.summaryPill}><Heart size={12} />{today.anniversaries}</span>
                    </div>
                    <div className={styles.summaryTotal}>{today.total} Total</div>
                </Link>
                <Link to="/special-moments/calendar" className={styles.summaryCard}>
                    <div className={styles.summaryCardLabel}>This Week</div>
                    <div className={styles.summaryCardRow}>
                        <span className={styles.summaryPill}><Cake size={12} />{week.birthdays}</span>
                        <span className={styles.summaryPill}><Heart size={12} />{week.anniversaries}</span>
                    </div>
                    <div className={styles.summaryTotal}>{week.total} Total</div>
                </Link>
                <Link to="/special-moments/calendar" className={styles.summaryCard}>
                    <div className={styles.summaryCardLabel}>This Month</div>
                    <div className={styles.summaryCardRow}>
                        <span className={styles.summaryPill}><Cake size={12} />{month.birthdays}</span>
                        <span className={styles.summaryPill}><Heart size={12} />{month.anniversaries}</span>
                    </div>
                    <div className={styles.summaryTotal}>{month.total} Total</div>
                </Link>
            </div>

            {preview.length > 0 && (
                <div className={styles.previewList}>
                    {preview.map((c) => (
                        <Link
                            key={c.client_store_relation_identifier}
                            to={`/special-moments/customer/${c.client_store_relation_identifier}`}
                            className={styles.previewCard}
                        >
                            <span className={styles.previewEmoji}>{emojiForIcon(c.icon)}</span>
                            <div className={styles.previewText}>
                                <div className={styles.previewName}>
                                    {c.name}
                                    {c.is_vip && (
                                        <span className={styles.vipBadge}>
                                            <Star size={10} fill="currentColor" /> VIP
                                        </span>
                                    )}
                                </div>
                                <div className={styles.previewOccasion}>{prettifyOccasionType(c.occasion_type)}</div>
                            </div>
                        </Link>
                    ))}
                    <Link to="/special-moments/inbox" className={styles.viewAllCard}>
                        View All <ArrowRight size={13} />
                    </Link>
                </div>
            )}
        </div>
    )
}

export default ClientEventsViewer
